import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerRequestAction } from '@ngrx/router-store';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ActiveToast, IndividualConfig, ToastrService } from 'ngx-toastr';
import { OperatorFunction, Subject, race, timer } from 'rxjs';
import { concatMap, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { getDeviceType } from 'ish-core/store/core/configuration';
import { mapToPayload } from 'ish-core/utils/operators';

import {
  MessagesPayloadType,
  displayErrorMessage,
  displayInfoMessage,
  displaySuccessMessage,
  displayWarningMessage,
} from './messages.actions';

@Injectable()
export class MessagesEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private translate: TranslateService,
    private toastr: ToastrService
  ) {}

  private applyStyle$ = new Subject<void>();
  private errorToastRef?: ActiveToast<any>;

  infoToast$ =
    !SSR &&
    createEffect(
      () =>
        this.actions$.pipe(
          ofType(displayInfoMessage),
          tap(() => this.clearAllToasts()), // clear all toasts before displaying a new one
          mapToPayload(),
          this.composeToastServiceArguments(0),
          map(args => this.toastr.info(...args)),
          tap(() => {
            this.applyStyle$.next();
          }),
          this.closeToastOnRouting()
        ),
      { dispatch: false }
    );

  errorToast$ =
    !SSR &&
    createEffect(
      () =>
        this.actions$.pipe(
          ofType(displayErrorMessage),
          tap(() => this.clearAllToasts()), // clear all toasts before displaying a new one
          mapToPayload(),
          this.composeToastServiceArguments(0),
          map(args => {
            const toast = this.toastr.error(...args);
            this.errorToastRef = toast; // store the reference
            return toast;
          }),
          tap(() => {
            this.applyStyle$.next();
          }),
          this.closeToastOnRouting()
        ),
      { dispatch: false }
    );

  warningToast$ =
    !SSR &&
    createEffect(
      () =>
        this.actions$.pipe(
          ofType(displayWarningMessage),
          tap(() => this.clearAllToasts()), // clear all toasts before displaying a new one
          mapToPayload(),
          this.composeToastServiceArguments(0),
          map(args => this.toastr.warning(...args)),
          tap(() => {
            this.applyStyle$.next();
          }),
          this.closeToastOnRouting()
        ),
      { dispatch: false }
    );

  successToast$ =
    !SSR &&
    createEffect(
      () =>
        this.actions$.pipe(
          ofType(displaySuccessMessage),
          tap(() => this.clearAllToasts()), // clear all toasts before displaying a new one
          mapToPayload(),
          filter(payload => !!payload?.message),
          tap(() => {
            if (this.errorToastRef) {
              this.errorToastRef.toastRef.manualClose();
              this.errorToastRef = undefined;
            }
          }),
          this.composeToastServiceArguments(),
          concatMap(args => {
            const toastRef = this.toastr.success(...args);
            const options = args[2];

            return options.timeOut === 0
              ? toastRef.onHidden.pipe(take(1))
              : race(toastRef.onHidden.pipe(take(1)), timer(options.timeOut));
          }),
          tap(() => {
            this.applyStyle$.next();
          })
        ),
      { dispatch: false }
    );
  //Commented for the changes made in new header and rewritten the effect below
  /*
  setToastStickyClass$ =
    !SSR &&
    createEffect(
      () =>
        combineLatest([this.store.pipe(select(isStickyHeader)), this.applyStyle$]).pipe(
          tap(([sticky]) => {
            const container = this.domService.getElementById('toast-container');
            if (container) {
              if (sticky) {
                this.domService.addClass(container, 'toast-sticky');
                container.classList.add('toast-sticky');
              } else {
                this.domService.removeClass(container, 'toast-sticky');
              }
            }
          })
        ),
      { dispatch: false }
    ); */

  private clearAllToasts(): void {
    this.toastr.clear();
    this.errorToastRef = undefined;
  }

  private closeToastOnRouting() {
    return switchMap((activeToast: ActiveToast<unknown>) =>
      this.actions$.pipe(
        ofType(routerRequestAction),
        take(1),
        tap(() => {
          activeToast.toastRef.manualClose();
        })
      )
    );
  }

  private composeToastServiceArguments(
    duration?: number
  ): OperatorFunction<MessagesPayloadType, [string, string, Partial<IndividualConfig>]> {
    return stream$ =>
      stream$.pipe(
        withLatestFrom(this.store.pipe(select(getDeviceType))),
        map(([payload, deviceType]) => {
          const timeOut = payload.duration ?? duration ?? 5000;
          return [
            // message translation

            this.translate.instant(payload.message, payload.messageParams),
            // title translation
            payload.title ? this.translate.instant(payload.title, payload.titleParams) : payload.title,
            // extra options
            {
              timeOut,
              extendedTimeOut: 3000,
              easeTime: 30,
              progressBar: false,
              closeButton: timeOut === 0,
              enableHtml: true,
              tapToDismiss: true,
              positionClass: deviceType === 'desktop' ? 'toast-top-full-width' : 'toast-bottom-center',
            },
          ];
        })
      );
  }
}
